import React, { useState, useEffect } from 'react';
import { getDashboards, createDashboard, deleteDashboard, setTickerTemplate, setOptionsTemplate, updateWidget } from '../../services/api';
import { Star, Info, Unlock, Search, X, TrendingUp } from 'lucide-react';
// import DashboardGrid from './DashboardGrid';
import SafeDashboardGrid from './SafeDashboardGrid';  // Using safe version
import AddWidgetModal from './AddWidgetModal';
import DashboardErrorBoundary from './ErrorBoundary';
import Tooltip from '../Tooltip';
import SearchableTicker from '../SearchableTicker';

function DashboardBuilder() {
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showNewDashboardInput, setShowNewDashboardInput] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [globalTicker, setGlobalTicker] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [showUndo, setShowUndo] = useState(false);

  useEffect(() => {
    loadDashboards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear global ticker and undo when switching dashboards
    if (activeDashboard) {
      setGlobalTicker(null);
      setUndoStack([]);
      setShowUndo(false);
    }
  }, [activeDashboard?.id]);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      const response = await getDashboards();
      setDashboards(response.data);
      
      // Auto-select first dashboard if available
      if (response.data.length > 0 && !activeDashboard) {
        setActiveDashboard(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!newDashboardName.trim()) return;

    try {
      const response = await createDashboard({ name: newDashboardName, description: '' });
      setDashboards([response.data, ...dashboards]);
      setActiveDashboard(response.data);
      setNewDashboardName('');
      setShowNewDashboardInput(false);
    } catch (error) {
      console.error('Error creating dashboard:', error);
      alert('Failed to create dashboard. Name might already exist.');
    }
  };

  const handleDeleteDashboard = async (dashboardId) => {
    if (!window.confirm('Are you sure you want to delete this dashboard?')) return;

    try {
      await deleteDashboard(dashboardId);
      const newDashboards = dashboards.filter(d => d.id !== dashboardId);
      setDashboards(newDashboards);
      
      if (activeDashboard?.id === dashboardId) {
        setActiveDashboard(newDashboards[0] || null);
      }
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      alert('Failed to delete dashboard');
    }
  };

  const handleWidgetAdded = async () => {
    // Only reload the active dashboard to preserve state
    if (activeDashboard) {
      try {
        const response = await getDashboards();
        const updatedDashboards = response.data;

        // Update the dashboards list
        setDashboards(updatedDashboards);

        // Find and update the active dashboard
        const updatedActive = updatedDashboards.find(d => d.id === activeDashboard.id);
        if (updatedActive) {
          setActiveDashboard(updatedActive);
        }
      } catch (error) {
        console.error('Error refreshing dashboard:', error);
      }
    }
  };

  const handleWidgetDeleted = async () => {
    // Same optimized refresh for widget deletion
    await handleWidgetAdded();
  };

  const handleWidgetUpdated = (updatedWidget) => {
    // Update the widget in the active dashboard without a full reload
    if (activeDashboard) {
      const updatedDashboard = {
        ...activeDashboard,
        widgets: activeDashboard.widgets.map(w =>
          w.id === updatedWidget.id ? updatedWidget : w
        )
      };
      setActiveDashboard(updatedDashboard);

      // Also update in the dashboards list
      setDashboards(dashboards.map(d =>
        d.id === activeDashboard.id ? updatedDashboard : d
      ));
    }
  };

  const handleSetAsTemplate = async (dashboardId) => {
    try {
      await setTickerTemplate(dashboardId);
      alert('Dashboard set as ticker template! Click any ticker in Screener/Watchlist to use this layout.');
      await loadDashboards(); // Refresh to show updated template status
    } catch (error) {
      console.error('Error setting template:', error);
      alert('Failed to set as template');
    }
  };

  const handleSetAsOptionsTemplate = async (dashboardId) => {
    try {
      await setOptionsTemplate(dashboardId);
      alert('Dashboard set as options template! Click any options contract in Screener/Watchlist to use this layout.');
      await loadDashboards(); // Refresh to show updated template status
    } catch (error) {
      console.error('Error setting options template:', error);
      alert('Failed to set as options template');
    }
  };

  const handleGlobalTickerChange = async (selectedTicker) => {
    if (!selectedTicker || !activeDashboard) return;

    const unlockedWidgets = activeDashboard.widgets.filter(w => w.use_dynamic_ticker);

    if (unlockedWidgets.length === 0) {
      alert('No unlocked widgets to update. Unlock widgets first by clicking the lock icon on each widget.');
      setGlobalTicker(null);
      return;
    }

    // Store previous state for undo
    const previousWidgets = unlockedWidgets.map(w => ({
      id: w.id,
      ticker: w.ticker
    }));

    try {
      // Update all unlocked widgets
      const updatePromises = unlockedWidgets.map(widget =>
        updateWidget(widget.id, {
          ...widget,
          ticker: selectedTicker.value.toUpperCase()
        })
      );

      await Promise.all(updatePromises);

      // Refresh dashboard
      await handleWidgetAdded();

      // Show undo toast
      setUndoStack([{
        timestamp: Date.now(),
        previousWidgets,
        newTicker: selectedTicker.value.toUpperCase()
      }]);
      setShowUndo(true);

      // Auto-hide undo after 5 seconds
      setTimeout(() => {
        setShowUndo(false);
        setUndoStack([]);
      }, 5000);

    } catch (error) {
      console.error('Error updating widgets:', error);
      alert('Failed to update some widgets');
    }
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;

    const lastAction = undoStack[0];

    try {
      const updatePromises = lastAction.previousWidgets.map(({ id, ticker }) =>
        updateWidget(id, {
          ...activeDashboard.widgets.find(w => w.id === id),
          ticker
        })
      );

      await Promise.all(updatePromises);
      await handleWidgetAdded();

      setShowUndo(false);
      setUndoStack([]);
      setGlobalTicker(null);
    } catch (error) {
      console.error('Error undoing changes:', error);
      alert('Failed to undo changes');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-text">Loading dashboards...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-builder-container">
      {/* Top Toolbar - Row 1: Title and Action Buttons */}
      <div className="bg-background border-b border-muted p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-brand-white">Dashboard Builder</h1>
          <div className="flex gap-3">
            <Tooltip
              content={
                <div>
                  <div className="font-semibold mb-1">Ticker Template Dashboard</div>
                  <div className="text-gray-300 text-xs">
                    When you click a ticker link from Screener or Watchlist, this dashboard layout will be used.
                    All unlocked widgets will automatically update to show the clicked ticker's data.
                  </div>
                </div>
              }
              placement="bottom"
            >
              <button
                onClick={() => handleSetAsTemplate(activeDashboard?.id)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition text-sm flex items-center gap-2"
                disabled={!activeDashboard}
              >
                {activeDashboard?.is_ticker_template ? (
                  <>
                    <Star className="w-4 h-4" fill="currentColor" />
                    Ticker Template
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4" />
                    Set as Ticker Template
                  </>
                )}
                <Info className="w-3 h-3 opacity-70" />
              </button>
            </Tooltip>
            <Tooltip
              content={
                <div>
                  <div className="font-semibold mb-1">Options Template Dashboard</div>
                  <div className="text-gray-300 text-xs">
                    When you click an options contract from Screener, this dashboard layout will be used.
                    Options chart widgets will show the clicked contract's premium history.
                  </div>
                </div>
              }
              placement="bottom"
            >
              <button
                onClick={() => handleSetAsOptionsTemplate(activeDashboard?.id)}
                className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded transition text-sm flex items-center gap-2"
                disabled={!activeDashboard}
              >
                {activeDashboard?.is_options_template ? (
                  <>
                    <TrendingUp className="w-4 h-4" fill="currentColor" />
                    Options Template
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4" />
                    Set as Options Template
                  </>
                )}
                <Info className="w-3 h-3 opacity-70" />
              </button>
            </Tooltip>
            <button
              onClick={() => setShowAddWidgetModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition"
              disabled={!activeDashboard}
            >
              + Add Widget
            </button>
          </div>
        </div>

        {/* Row 2: Dashboard List - Horizontal Layout */}
        <div className="flex items-center gap-3">
          <span className="text-text font-semibold text-sm whitespace-nowrap">My Dashboards:</span>

          {/* New Dashboard Button/Input */}
          {!showNewDashboardInput ? (
            <button
              onClick={() => setShowNewDashboardInput(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded transition text-sm whitespace-nowrap"
            >
              + New Dashboard
            </button>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                placeholder="Dashboard name..."
                className="px-3 py-1.5 bg-muted border border-muted rounded text-text text-sm w-48"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleCreateDashboard()}
              />
              <button
                onClick={handleCreateDashboard}
                className="bg-primary text-white px-3 py-1.5 rounded text-sm hover:bg-primary/80"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowNewDashboardInput(false);
                  setNewDashboardName('');
                }}
                className="bg-muted text-text px-3 py-1.5 rounded text-sm hover:bg-muted/80"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Dashboard Pills - Horizontal Scrollable */}
          <div className="flex gap-2 overflow-x-auto flex-1 pb-1">
            {dashboards.length === 0 ? (
              <span className="text-text/60 text-sm italic">No dashboards yet</span>
            ) : (
              dashboards.map((dashboard) => (
                <div
                  key={dashboard.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer transition whitespace-nowrap ${
                    activeDashboard?.id === dashboard.id
                      ? 'bg-primary text-white'
                      : 'bg-muted text-text hover:bg-muted/80'
                  }`}
                  onClick={() => setActiveDashboard(dashboard)}
                >
                  {dashboard.is_ticker_template && (
                    <Star className="w-3 h-3 text-yellow-400" fill="currentColor" title="Ticker Template" />
                  )}
                  {dashboard.is_options_template && (
                    <TrendingUp className="w-3 h-3 text-green-400" title="Options Template" />
                  )}
                  <span className="font-medium text-sm">{dashboard.name}</span>
                  <span className="text-xs opacity-70">({dashboard.widget_count || 0})</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDashboard(dashboard.id);
                    }}
                    className="text-xs opacity-70 hover:opacity-100 ml-1"
                    title="Delete dashboard"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Global Ticker Search Bar */}
      {activeDashboard && (
        <div className="bg-background border-b border-muted px-4 py-3">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-text whitespace-nowrap flex items-center gap-2">
              <Unlock className="w-4 h-4 text-blue-400" />
              Update All Unlocked:
            </label>

            <div className="w-64">
              <SearchableTicker
                value={globalTicker}
                onChange={handleGlobalTickerChange}
                placeholder="Search ticker..."
              />
            </div>

            <span className="text-xs text-text/40 italic">
              Changes all unlocked widgets instantly
            </span>
          </div>
        </div>
      )}

      {/* Main Grid Area - Full Width */}
      <div className="p-4">
        <DashboardErrorBoundary>
          {activeDashboard ? (
            <SafeDashboardGrid
              dashboard={activeDashboard}
              onWidgetDeleted={handleWidgetDeleted}
              onWidgetUpdated={handleWidgetUpdated}
            />
          ) : (
            <div className="bg-background border border-muted rounded p-8 text-center">
              <p className="text-text/60 mb-4">No dashboard selected</p>
              <button
                onClick={() => setShowNewDashboardInput(true)}
                className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/80 transition"
              >
                Create Your First Dashboard
              </button>
            </div>
          )}
        </DashboardErrorBoundary>
      </div>

      {/* Add Widget Modal */}
      {showAddWidgetModal && activeDashboard && (
        <AddWidgetModal
          dashboard={activeDashboard}
          onClose={() => setShowAddWidgetModal(false)}
          onWidgetAdded={handleWidgetAdded}
        />
      )}

      {/* Undo Toast */}
      {showUndo && undoStack.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className="bg-gray-900 border border-primary rounded-lg shadow-xl px-4 py-3 flex items-center gap-3">
            <span className="text-text text-sm">
              Updated {undoStack[0].previousWidgets.length} widget{undoStack[0].previousWidgets.length !== 1 ? 's' : ''} to{' '}
              <strong className="text-primary">{undoStack[0].newTicker}</strong>
            </span>
            <button
              onClick={handleUndo}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition"
            >
              Undo
            </button>
            <button
              onClick={() => {
                setShowUndo(false);
                setUndoStack([]);
              }}
              className="text-text/60 hover:text-text transition"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardBuilder;
